import NetworkIcon from "@core/network-icon";
import { AITimelineContent } from "../../types/ai-templates";
import { motion } from "framer-motion";
import { useState } from "react";

interface TimelineTemplate4Props {
  content: AITimelineContent;
  onUpdate: (newContent: AITimelineContent) => void;
}

export function TimelineTemplate4({
  content,
  onUpdate,
}: TimelineTemplate4Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");

  // Handle event update
  const handleEventUpdate = (
    index: number,
    data: { title?: string; description?: string },
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index] = {
        ...newContent.steps[index],
        ...data,
      };
      onUpdate(newContent);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  // Ensure events array exists and has at least 4 events to match SVG
  if (!content.steps || content.steps.length < 4) {
    return (
      <div className="p-4 text-center text-gray-500">
        At least 4 timeline events are required to display.
      </div>
    );
  }

  // Define positions and styles for icons, titles, descriptions, numbers, and shapes
  const eventData = [
    {
      iconTransform: "translate(440, 458)",
      textTransform: "translate(116, 452)",
      descTransform: "translate(116, 488)",
      numberTransform: "translate(44, 452)",
      iconStroke:
        content.steps[0].color ||
        content.genericColors[0 % content.genericColors.length] ||
        "#6D4C41",
      iconStrokeOverlay: `${content.steps[0].color || content.genericColors[0 % content.genericColors.length] || "#6D4C41"}80`,
      fill:
        content.steps[0].color ||
        content.genericColors[0 % content.genericColors.length] ||
        "#6D4C41",
      fillOverlay: "url(#crawlingPattern-0060fba2)",
      index: 0,
      shapeFill: "#E8D9D4",
      shapeFillOverlay: "url(#crawlingPattern-647ea91a)",
      shapeTransform: "translate(374, 428)",
      textLineTransform: "translate(98, 452)",
      textLineFill: "#E8D9D4",
      textLineFillOverlay: "url(#crawlingPattern-647ea91a)",
      textLineStroke:
        content.steps[0].color ||
        content.genericColors[0 % content.genericColors.length] ||
        "#6D4C41",
      textLineStrokeOverlay: `${content.steps[0].color || content.genericColors[0 % content.genericColors.length] || "#6D4C41"}80`,
      descColor: "#4B3C34",
    },
    {
      iconTransform: "translate(530, 350)",
      textTransform: "translate(206, 338)",
      descTransform: "translate(206, 374)",
      numberTransform: "translate(134, 338)",
      iconStroke:
        content.steps[1].color ||
        content.genericColors[1 % content.genericColors.length] ||
        "#795548",
      iconStrokeOverlay: `${content.steps[1].color || content.genericColors[1 % content.genericColors.length] || "#795548"}80`,
      fill:
        content.steps[1].color ||
        content.genericColors[1 % content.genericColors.length] ||
        "#795548",
      fillOverlay: "url(#crawlingPattern-00a3d9d9)",
      index: 1,
      shapeFill: "#EBDDD6",
      shapeFillOverlay: "url(#crawlingPattern-5a87971a)",
      shapeTransform: "translate(464, 320)",
      textLineTransform: "translate(188, 338)",
      textLineFill: "#EBDDD6",
      textLineFillOverlay: "url(#crawlingPattern-5a87971a)",
      textLineStroke:
        content.steps[1].color ||
        content.genericColors[1 % content.genericColors.length] ||
        "#795548",
      textLineStrokeOverlay: `${content.steps[1].color || content.genericColors[1 % content.genericColors.length] || "#795548"}80`,
      descColor: "#553E36",
    },
    {
      iconTransform: "translate(620, 242)",
      textTransform: "translate(296, 230)",
      descTransform: "translate(296, 266)",
      numberTransform: "translate(224, 230)",
      iconStroke:
        content.steps[2].color ||
        content.genericColors[2 % content.genericColors.length] ||
        "#8D6E63",
      iconStrokeOverlay: `${content.steps[2].color || content.genericColors[2 % content.genericColors.length] || "#8D6E63"}80`,
      fill:
        content.steps[2].color ||
        content.genericColors[2 % content.genericColors.length] ||
        "#8D6E63",
      fillOverlay: "url(#crawlingPattern-00b15c75)",
      index: 2,
      shapeFill: "#EFE2DD",
      shapeFillOverlay: "url(#crawlingPattern-65927c1a)",
      shapeTransform: "translate(554, 212)",
      textLineTransform: "translate(278, 230)",
      textLineFill: "#EFE2DD",
      textLineFillOverlay: "url(#crawlingPattern-65927c1a)",
      textLineStroke:
        content.steps[2].color ||
        content.genericColors[2 % content.genericColors.length] ||
        "#8D6E63",
      textLineStrokeOverlay: `${content.steps[2].color || content.genericColors[2 % content.genericColors.length] || "#8D6E63"}80`,
      descColor: "#634C45",
    },
    {
      iconTransform: "translate(710, 134)",
      textTransform: "translate(386, 122)",
      descTransform: "translate(386, 158)",
      numberTransform: "translate(314, 122)",
      iconStroke:
        content.steps[3].color ||
        content.genericColors[3 % content.genericColors.length] ||
        "#A1887F",
      iconStrokeOverlay: `${content.steps[3].color || content.genericColors[3 % content.genericColors.length] || "#A1887F"}80`,
      fill:
        content.steps[3].color ||
        content.genericColors[3 % content.genericColors.length] ||
        "#A1887F",
      fillOverlay: "url(#crawlingPattern-5c87005e)",
      index: 3,
      shapeFill: "#F2EAE7",
      shapeFillOverlay: "url(#crawlingPattern-818f641a)",
      shapeTransform: "translate(644, 104)",
      textLineTransform: "translate(368, 122)",
      textLineFill: "#F2EAE7",
      textLineFillOverlay: "url(#crawlingPattern-818f641a)",
      textLineStroke:
        content.steps[3].color ||
        content.genericColors[3 % content.genericColors.length] ||
        "#A1887F",
      textLineStrokeOverlay: `${content.steps[3].color || content.genericColors[3 % content.genericColors.length] || "#A1887F"}80`,
      descColor: "#715E58",
    },
  ];

  // Handle the fifth event
  const extraEvents = content.steps.slice(4).map((event, index) => ({
    index: 4 + index,
    title: event.title,
    description: event.description,
    iconId: event.iconId,
    iconDescription: event.icon_description,
    color:
      event.color ||
      content.genericColors[(4 + index) % content.genericColors.length] ||
      "#BCAAA4",
    descColor: "#836F6B",
  }));

  return (
    <div className="p-6 overflow-hidden">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="mx-auto"
      >
        <svg
          width="888"
          height="636"
          viewBox="0 0 888 636"
          style={{
            fill: "none",
            stroke: "none",
            fillRule: "evenodd",
            clipRule: "evenodd",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeMiterlimit: 1.5,
          }}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
            `}
          </style>
          <defs>
            <pattern
              id="crawlingPattern-8787871a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#8787871a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <symbol id="crawlingSymbol">
              <g>
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from="0 0"
                  to="-12 0"
                  dur="0.4s"
                  repeatCount="indefinite"
                ></animateTransform>
                <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z"></path>
              </g>
            </symbol>
            <pattern
              id="crawlingPattern-647ea91a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#647ea91a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5a87971a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5a87971a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-65927c1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#65927c1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-818f641a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#818f641a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-0000001b"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#0000001b"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-00a3d9d9"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#00a3d9d9"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-00b15c75"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#00b15c75"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-0060fba2"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#0060fba2"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5c87005e"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5c87005e"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
            <pattern
              id="crawlingPattern-5f5f5f1a"
              x="0"
              y="0"
              width="12"
              height="12"
              patternUnits="userSpaceOnUse"
              fill="#5f5f5f1a"
            >
              <use href="#crawlingSymbol"></use>
            </pattern>
          </defs>
          <g transform="translate(374, 158)">
            <g stroke="none" fill="#E8D9D4">
              <path d="M 460 10L 370 64.0029L 370 118L 280 172L 280 226L 190 280L 190 334L 100 388L 10 334L 10 349.609L 100 403.609L 202 342.109L 202 288.009L 292 234L 292 180.009L 382 125.982L 382 72.0119L 460 25.2012L 460 10Z"></path>
            </g>
            <g stroke="none" fill="url(#crawlingPattern-8787871a)">
              <path d="M 460 10L 370 64.0029L 370 118L 280 172L 280 226L 190 280L 190 334L 100 388L 10 334L 10 349.609L 100 403.609L 202 342.109L 202 288.009L 292 234L 292 180.009L 382 125.9815L 382 72.0119L 460 25.2012L 460 10Z"></path>
            </g>
            <g
              fill="none"
              stroke={
                content.steps[0].color ||
                content.genericColors[0 % content.genericColors.length] ||
                "#6D4C41"
              }
              strokeWidth="2"
            >
              <path d="M 460 10L 370 64.0029L 370 118L 280 172L 280 226L 190 280L 190 334L 100 388L 10 334L 10 349.6089L 100 403.6089L 202 342.1089L 202 288.009L 292 234L 292 180.009L 382 125.9815L 382 72.0119L 460 25.2012L 460 10Z"></path>
            </g>
            <g
              fill="none"
              stroke={`${content.steps[0].color || content.genericColors[0 % content.genericColors.length] || "#6D4C41"}80`}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 460 10L 370 64.0029L 370 118L 280 172L 280 226L 190 280L 190 334L 100 388L 10 334L 10 349.6089L 100 403.6089L 202 342.1089L 202 288.009L 292 234L 292 180.009L 382 125.9815L 382 72.0119L 460 25.2012L 460 10Z"></path>
            </g>
            <g stroke="none" fill="#E8D9D4">
              <path d="M 370 64L 280 10L 280 64L 370 118L 370 64Z"></path>
              <path d="M 280 172L 190 118L 190 172L 280 226L 280 172Z"></path>
              <path d="M 190 280L 100 226L 100 281L 190 334L 190 280Z"></path>
            </g>
            <g stroke="none" fill="url(#crawlingPattern-8787871a)">
              <path d="M 370 64L 280 10L 280 64L 370 118L 370 64Z"></path>
              <path d="M 280 172L 190 118L 190 172L 280 226L 280 172Z"></path>
              <path d="M 190 280L 100 226L 100 281L 190 334L 190 280Z"></path>
            </g>
            <g
              fill="none"
              stroke={
                content.steps[0].color ||
                content.genericColors[0 % content.genericColors.length] ||
                "#6D4C41"
              }
              strokeWidth="2"
            >
              <path d="M 370 64L 280 10L 280 64L 370 118L 370 64Z"></path>
              <path d="M 280 172L 190 118L 190 172L 280 226L 280 172Z"></path>
              <path d="M 190 280L 100 226L 100 281L 190 334L 190 280Z"></path>
            </g>
            <g
              fill="none"
              stroke={`${content.steps[0].color || content.genericColors[0 % content.genericColors.length] || "#6D4C41"}80`}
              strokeWidth="2"
              strokeDasharray="4,6"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="30;0"
                dur="1s"
                repeatCount="indefinite"
                begin="0"
              ></animate>
              <path d="M 370 64L 280 10L 280 64L 370 118L 370 64Z"></path>
              <path d="M 280 172L 190 118L 190 172L 280 226L 280 172Z"></path>
              <path d="M 190 280L 100 226L 100 281L 190 334L 190 280Z"></path>
            </g>
          </g>
          {eventData.map((data) => (
            <g key={`event-${data.index}`}>
              <g transform={data.shapeTransform}>
                <g stroke="none" fill={data.shapeFill}>
                  <path d="M 100 10L 10 64L 100 118L 190 64L 100 10ZM 29 64L 100 22L 171 64L 100 106L 29 64Z"></path>
                </g>
                <g stroke="none" fill={data.shapeFillOverlay}>
                  <path d="M 100 10L 10 64L 100 118L 190 64L 100 10ZM 29 64L 100 22L 171 64L 100 106L 29 64Z"></path>
                </g>
                <g fill="none" stroke={data.iconStroke} strokeWidth="2">
                  <path d="M 100 10L 10 64L 100 118L 190 64L 100 10ZM 29 64L 100 22L 171 64L 100 106L 29 64Z"></path>
                </g>
                <g
                  fill="none"
                  stroke={data.iconStrokeOverlay}
                  strokeWidth="2"
                  strokeDasharray="4,6"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    values="30;0"
                    dur="1s"
                    repeatCount="indefinite"
                    begin="0"
                  ></animate>
                  <path d="M 100 10L 10 64L 100 118L 190 64L 100 10ZM 29 64L 100 22L 171 64L 100 106L 29 64Z"></path>
                </g>
              </g>
              <g transform={data.textLineTransform}>
                <g stroke="none" fill={data.textLineFill}>
                  <path d="M 10 10L 16 10L 16 82L 10 82Z"></path>
                </g>
                <g stroke="none" fill={data.textLineFillOverlay}>
                  <path d="M 10 10L 16 10L 16 82L 10 82Z"></path>
                </g>
                <g fill="none" stroke={data.textLineStroke} strokeWidth="2">
                  <path d="M 10 10L 16 10L 16 82L 10 82Z"></path>
                </g>
                <g
                  fill="none"
                  stroke={data.textLineStrokeOverlay}
                  strokeWidth="2"
                  strokeDasharray="4,6"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    values="30;0"
                    dur="1s"
                    repeatCount="indefinite"
                    begin="0"
                  ></animate>
                  <path d="M 10 10L 16 10L 16 82L 10 82Z"></path>
                </g>
              </g>
              <g transform={data.iconTransform}>
                <foreignObject x="0" y="0" width="68" height="68">
                  <div
                    style={{
                      width: "68px",
                      height: "68px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        content.steps[data.index].color ||
                        content.genericColors[
                          data.index % content.genericColors.length
                        ] ||
                        "#9CA3AF",
                      borderRadius: "50%",
                    }}
                    aria-label={
                      content.steps[data.index].icon_description ||
                      content.steps[data.index].title
                    }
                  >
                    <NetworkIcon
                      value={
                        content.steps[data.index].icon_description ??
                        content.steps[data.index].iconId
                      }
                      type="LUCIDE"
                      style={{
                        width: "32px",
                        height: "32px",
                        color: "#ffffff",
                      }}
                    />
                  </div>
                </foreignObject>
              </g>
              <g transform={data.textTransform}>
                <foreignObject x="12" y="0" width="230" height="50">
                  {editingIndex === data.index && editingField === "title" ? (
                    <input
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (tempValue !== content.steps[data.index].title) {
                          handleEventUpdate(data.index, { title: tempValue });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.currentTarget.blur();
                        }
                      }}
                      style={{
                        font: "bold 20px Roboto, sans-serif",
                        color: data.fill,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        width: "100%",
                        padding: "0",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        font: "bold 20px Roboto, sans-serif",
                        color: data.fill,
                        cursor: "text",
                        padding: "0",
                      }}
                      onClick={() => {
                        setEditingIndex(data.index);
                        setEditingField("title");
                        setTempValue(content.steps[data.index].title);
                      }}
                    >
                      {content.steps[data.index].title}
                    </div>
                  )}
                </foreignObject>
              </g>
              <g transform={data.descTransform}>
                <foreignObject x="12" y="0" width="230" height="80">
                  {editingIndex === data.index &&
                  editingField === "description" ? (
                    <textarea
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (
                          tempValue !== content.steps[data.index].description
                        ) {
                          handleEventUpdate(data.index, {
                            description: tempValue,
                          });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.height = "auto";
                        e.currentTarget.style.height =
                          e.currentTarget.scrollHeight + "px";
                      }}
                      style={{
                        font: "15px Roboto, sans-serif",
                        color: data.descColor,
                        background: "transparent",
                        border: "none",
                        outline: "none",
                        width: "100%",
                        resize: "none",
                        padding: "0",
                      }}
                      rows={2}
                    />
                  ) : (
                    <div
                      style={{
                        font: "15px Roboto, sans-serif",
                        color: data.descColor,
                        cursor: "text",
                        padding: "0",
                        whiteSpace: "pre-wrap",
                      }}
                      onClick={() => {
                        setEditingIndex(data.index);
                        setEditingField("description");
                        setTempValue(content.steps[data.index].description);
                      }}
                    >
                      {content.steps[data.index].description}
                    </div>
                  )}
                </foreignObject>
              </g>
              <g transform={data.numberTransform}>
                <g stroke="none" fill={data.fill}>
                  <text
                    style={{
                      font: "bold 30px Roboto, sans-serif",
                      whiteSpace: "pre",
                    }}
                  >
                    <tspan x="13.39" y="45.5" dominantBaseline="ideographic">
                      {data.index + 1}
                    </tspan>
                  </text>
                </g>
              </g>
            </g>
          ))}
          <g transform="translate(460, 50)">
            <g
              stroke="none"
              fill={
                content.steps[0].color ||
                content.genericColors[0 % content.genericColors.length] ||
                "#6D4C41"
              }
            >
              <text
                style={{ font: "20px Roboto, sans-serif", whiteSpace: "pre" }}
              >
                <tspan x="14.08" y="33.5" dominantBaseline="ideographic">
                  {content.title}
                </tspan>
              </text>
            </g>
          </g>
        </svg>
        {extraEvents.length > 0 && (
          <div className="mt-4">
            {extraEvents.map((event) => (
              <motion.div
                key={`extra-event-${event.index}`}
                className="flex flex-col items-center p-4"
                variants={itemVariants}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-2 shadow-lg"
                  style={{
                    backgroundColor: event.color,
                  }}
                  aria-label={event.iconDescription || event.title}
                >
                  <NetworkIcon
                    value={event.iconDescription ?? event.iconId}
                    type="LUCIDE"
                    className="w-8 h-8"
                  />
                </div>
                {editingIndex === event.index && editingField === "title" ? (
                  <input
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingIndex(null);
                      setEditingField(null);
                      if (tempValue !== event.title) {
                        handleEventUpdate(event.index, { title: tempValue });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.currentTarget.blur();
                      }
                    }}
                    className="font-semibold h-10 flex items-center focus:outline-none focus:ring-2 focus:ring-primary bg-transparent rounded px-1"
                    style={{ color: event.color }}
                  />
                ) : (
                  <h4
                    className="font-semibold h-10 flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:bg-gray-100 rounded px-1 cursor-text"
                    style={{ color: event.color }}
                    onClick={() => {
                      setEditingIndex(event.index);
                      setEditingField("title");
                      setTempValue(event.title);
                    }}
                  >
                    {event.title}
                  </h4>
                )}
                {editingIndex === event.index &&
                editingField === "description" ? (
                  <textarea
                    autoFocus
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={() => {
                      setEditingIndex(null);
                      setEditingField(null);
                      if (tempValue !== event.description) {
                        handleEventUpdate(event.index, {
                          description: tempValue,
                        });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.height = "auto";
                      e.currentTarget.style.height =
                        e.currentTarget.scrollHeight + "px";
                    }}
                    rows={2}
                    className="w-full focus:outline-none focus:ring-2 focus:ring-primary bg-transparent rounded px-1 text-center text-sm"
                    style={{ color: event.descColor }}
                  />
                ) : (
                  <p
                    className="focus:outline-none focus:ring-2 focus:ring-primary focus:bg-gray-100 rounded px-1 cursor-text text-center text-sm"
                    style={{ color: event.descColor }}
                    onClick={() => {
                      setEditingIndex(event.index);
                      setEditingField("description");
                      setTempValue(event.description);
                    }}
                  >
                    {event.description}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
